import { createContext, Dispatch, ReactNode, useContext, useEffect, useReducer, useRef } from "react"
import { UseMutationResult } from "react-query"
import { AuthenticationContext } from "../../AuthenticationContext"
import { TenantDTO, TenantUpdateDTO } from "../MyTenant/tenantsModel"
import { useSaveTenantMutation, useTenantsQuery } from "../MyTenant/tenantsQueries"
import { AccountSetupAction, accountSetupReducer, AccountSetupState, getInitialState } from "./accountSetupReducer"

type AccountSetupContextData = {
  state: AccountSetupState
  dispatch: Dispatch<AccountSetupAction>
  tenantData: TenantDTO | undefined
  saveTenantMutation: UseMutationResult<TenantDTO, unknown, TenantUpdateDTO, unknown>
  saveLogoMutation: UseMutationResult<TenantDTO, unknown, TenantUpdateDTO, unknown>
}

export const AccountSetupContext = createContext<AccountSetupContextData | undefined>(undefined)

type Props = {
  children: ReactNode
}

export const AccountSetupProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(accountSetupReducer, getInitialState())
  const { currentUser } = useContext(AuthenticationContext)
  const hasInitialized = useRef(false)

  const tenantQuery = useTenantsQuery(currentUser?.tenant.id)
  const saveTenantMutation = useSaveTenantMutation(() => {
    dispatch({ type: 'NEXT_STEP' })
  })
  const saveLogoMutation = useSaveTenantMutation()

  // Initialize state from tenant data only once on initial load
  useEffect(() => {
    if (tenantQuery.data && !hasInitialized.current) {
      hasInitialized.current = true
      dispatch({ type: 'INIT_FROM_TENANT', payload: tenantQuery.data })
    }
  }, [tenantQuery.data])

  const contextValue: AccountSetupContextData = {
    state,
    dispatch,
    tenantData: tenantQuery.data,
    saveTenantMutation,
    saveLogoMutation,
  }

  return (
    <AccountSetupContext.Provider value={contextValue}>
      {children}
    </AccountSetupContext.Provider>
  )
}

export const useAccountSetup = () => {
  const context = useContext(AccountSetupContext)
  if (!context) {
    throw new Error('useAccountSetup must be used within AccountSetupProvider')
  }
  return context
}
